// Tooltip (DESIGN §3.7.5): Radix Tooltip, 500 ms Verzögerung, auch bei Tastatur-Fokus.
// Dunkel: Fläche `overlay`; hell: dunkler Tooltip (`fg` mit Text `page`).
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import './_lib/motion.css';
import { cn } from '../basis/kern';

const InProvider = createContext(false);

/** Gemeinsamer Provider (sitzt in den Shells); danach öffnen weitere Tooltips ohne Verzögerung. */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <InProvider.Provider value={true}>
      <TooltipPrimitive.Provider delayDuration={500} skipDelayDuration={300}>
        {children}
      </TooltipPrimitive.Provider>
    </InProvider.Provider>
  );
}

export interface TooltipProps {
  content: ReactNode;
  /** Ein einzelnes fokussierbares Element (Radix `asChild`). */
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Tastenkürzel, rechts im Tooltip als Kbd. */
  shortcut?: string;
  /** Unterdrückt den Tooltip (z. B. wenn das Label schon sichtbar ist). */
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const tooltipContentClass = cn(
  'bar-st-pop z-(--bar-z-tooltip) max-w-60 rounded-sm bg-overlay px-2.5 py-1.5 text-small text-fg shadow-2',
  '[[data-theme=light]_&]:bg-fg [[data-theme=light]_&]:text-page',
);

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  shortcut,
  disabled,
  open,
  onOpenChange,
}: TooltipProps) {
  const inProvider = useContext(InProvider);
  if (disabled || content === null || content === undefined || content === '') return children;
  const tooltip = (
    <TooltipPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={8}
          collisionPadding={8}
          className={tooltipContentClass}
        >
          <span className="flex items-center gap-2">
            <span>{content}</span>
            {shortcut ? (
              <kbd className="rounded-xs bg-pill-card px-1 font-sans text-chip text-fg-secondary [[data-theme=light]_&]:bg-fg-secondary [[data-theme=light]_&]:text-page">
                {shortcut}
              </kbd>
            ) : null}
          </span>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
  return inProvider ? tooltip : <TooltipProvider>{tooltip}</TooltipProvider>;
}
